import React from 'react';
import { isJapanTraffic } from '../../../_helpers/japanTraffic.js';
import { isChordLine } from '../../../_helpers/lyricSheet.js';
import { useLyricsImpression } from '../../../hooks/useLyricsImpression.js';
import { LyricsStatus, isProviderAnswer, useSongLyrics } from '../../../hooks/useSongLyrics.js';
import LyricsAttribution from '../lyrics/LyricsAttribution.jsx';

/**
 * The first sung line of a song (mockup 22's lyric line, ENG-709), from
 * `GET /songs/{id}/lyrics`, which needs a session: signed out, nothing shows.
 * A line of the provider's words carries what the licence asks of any
 * (ENG-521): the credit and copyright, the impression, an excerpt only with
 * its backlink, and no copying from Japan.
 */
export const firstSungLine = (body) => {
  if (typeof body !== 'string') return '';
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || /^\[.*\]$/.test(line) || /^\(.*\)$/.test(line) || isChordLine(line)) continue;
    if (/^\*{3}/.test(line)) break; // the provider's own trailing notice
    return line;
  }
  return '';
};

const SongLyricLine = ({ songId, enabled = true }) => {
  const { status, lyrics } = useSongLyrics(songId, { enabled });
  const line = status === LyricsStatus.READY ? firstSungLine(lyrics?.body) : '';
  const isProvider = isProviderAnswer(lyrics);
  // An excerpt of the provider's words may only be shown with its way to the rest.
  const shown = Boolean(line) && !(isProvider && lyrics?.partial && !lyrics?.backlinkUrl);
  useLyricsImpression(shown && isProvider ? lyrics?.trackingScriptUrl ?? null : null, {
    pixelUrl: lyrics?.trackingPixelUrl ?? null,
    visible: shown,
  });
  if (!shown) return null;

  const guard = isProvider && isJapanTraffic() ? (event) => event.preventDefault() : undefined;
  return (
    <figure className="song-page-lyric" data-testid="song-page-lyric">
      <blockquote
        className={`song-page-lyric-line${guard ? ' song-page-lyric-line--guarded' : ''}`}
        lang={lyrics?.language || undefined}
        onCopy={guard}
        onCut={guard}
        onDragStart={guard}
      >
        “{line}”
      </blockquote>
      {isProvider && <LyricsAttribution copyright={lyrics?.copyright || null} backlinkUrl={lyrics?.backlinkUrl || null} />}
    </figure>
  );
};

export default SongLyricLine;
